import type { KitStory } from './storyTypes';
import { kitWords, type KitTextLocale } from '../kitText';
import { storyPillText } from './storyCopy';
import { outBack, progress, round3 } from './videoMotion';

/**
 * The lyric moment (design `asset-redesign/u Lyric moment - new`, owner answers 2026-10-08): the
 * lyric Story in motion. The picked lines (one line or a range; the default is the stanza at the
 * hook) move as one column with the lyric motion of the theme in x 140 to 940, y 300 to 1000; each
 * word lights 0.2 s before it is sung. The cover row names the song one time (y 1050 to 1250), the
 * CTA pill by date ("Coming soon" with no published pre-save page) and "Link in bio · <SmartLink>"
 * show from frame 0, the Free watermark sits under the link line, and the last 1.5 s are the end
 * card: the column stops and the pill grows to 1.12. The lyric keeps the artist's own case.
 */

/** The texts and the timing of one lyric moment (`plan.short`, kind `lyricMoment`). */
export interface KitLyricMomentVideo {
  kind: 'lyricMoment';
  /** The cover row and the CTA group (format `moment916`; the lyric itself is in the lyric layers). */
  row: KitStory;
  /** The end card starts here (the length less 1.5 s): the column stops, the pill grows. */
  endCardSec: number;
  /** The downbeats of the window, in video time: the cover of the row kicks on each one. */
  downbeats: number[];
  /** The poster frame: 1 s, the first line lit, the cover row and the CTA in. */
  posterSec: number;
}

/** The poster of a lyric moment: 1 s into the video (the first word sings at 0.35 s). */
export const LYRIC_MOMENT_POSTER_SEC = 1;

/** The window starts this long before the first sung word (owner default 4 of page u). */
export const LYRIC_MOMENT_PRE_ROLL_SEC = 0.35;
/** A beat this close to the pre-roll start takes its place. */
const BEAT_SNAP_SEC = 0.15;
/** The end card: the last 1.5 s. */
export const LYRIC_MOMENT_END_CARD_SEC = 1.5;
/** The default lines start on a stanza that starts at most this long before the hook. */
const STANZA_BEFORE_HOOK_SEC = 3;

/** The lyric column of design u (px of 1080 x 1920): the sung line ends at y 710, the next starts at 770. */
export const LYRIC_MOMENT_COLUMN = { x: 140, top: 300, bottom: 1000, w: 800, sungBottom: 710, gap: 60 };
/** The full size of the lyric before the display factor of the theme. */
export const LYRIC_MOMENT_LYRIC_PX = 104;

/**
 * The start of the window of a lyric moment: 0.35 s before the first sung word, on a beat when one
 * is within 0.15 s of it; never before 0. Pure.
 */
export function lyricMomentWindowStart(firstWordSec: number, beats: readonly number[]): number {
  const wanted = Math.max(0, firstWordSec - LYRIC_MOMENT_PRE_ROLL_SEC);
  const beat = beats
    .filter((time) => Math.abs(time - wanted) <= BEAT_SNAP_SEC && time < firstWordSec)
    .reduce<
      number | null
    >((best, time) => (best === null || Math.abs(time - wanted) < Math.abs(best - wanted) ? time : best), null);
  return round3(Math.max(0, beat ?? wanted));
}

/** One line of a lyric sheet with its time (the lines of `LyricTimingV1`). */
export interface MomentLine {
  index: number;
  startMs: number | null;
  /** A blank line or a section label comes before the line: a stanza starts here. */
  gapBefore?: boolean;
}

/**
 * The default lines of a lyric moment (owner answers 1 and 2 of page u): the stanza at the hook (the
 * first line with a gap before it that starts at most 3 s before the hook, else the first line after
 * the hook), then every next line that starts before the end card of a window of `lengthSec` that
 * starts 0.35 s before it. With no hook, the first timed line. Null with no timed line. Pure.
 */
export function lyricMomentDefaultLines(
  lines: readonly MomentLine[],
  hookStartSec: number | null,
  lengthSec: number
): { lineFrom: number; lineTo: number } | null {
  const timed = lines.filter((line): line is MomentLine & { startMs: number } => line.startMs !== null);
  const firstTimed = timed[0];
  if (firstTimed === undefined) {
    return null;
  }
  const hookMs = hookStartSec === null ? firstTimed.startMs : hookStartSec * 1000;
  const stanza = timed.find(
    (line) => line.gapBefore === true && line.startMs >= hookMs - STANZA_BEFORE_HOOK_SEC * 1000
  );
  const afterHook = timed.find((line) => line.startMs >= hookMs);
  const first =
    stanza !== undefined && (afterHook === undefined || stanza.startMs <= afterHook.startMs + 1)
      ? stanza
      : (afterHook ?? timed[timed.length - 1] ?? firstTimed);
  const startMs = first.startMs - LYRIC_MOMENT_PRE_ROLL_SEC * 1000;
  const endMs = startMs + (lengthSec - LYRIC_MOMENT_END_CARD_SEC) * 1000;
  const inside = timed.filter((line) => line.index >= first.index && line.startMs < endMs);
  const last = inside[inside.length - 1] ?? first;
  return { lineFrom: first.index, lineTo: last.index };
}

/**
 * The cover row and the CTA group of a lyric moment: the title one time, the pill by the date and
 * the pre-save state, and "Link in bio" before the SmartLink address. Pure.
 */
export function buildLyricMomentRow(input: {
  title: string;
  artist: string;
  releaseDate: string | null;
  today: string;
  link: string | null;
  presave?: boolean;
  /** The language of the words; absent: English. */
  locale?: KitTextLocale | null;
}): KitStory {
  return {
    format: 'moment916',
    // The countdown stack with no number, label or ring: the cover row alone, above the CTA.
    moment: 'countdown',
    title: input.title,
    artist: input.artist,
    kicker: null,
    hero: null,
    nowWord: null,
    dayLabel: null,
    when: null,
    cta: storyPillText('lyric', input.releaseDate, input.today, input.presave, input.locale),
    link: input.link,
    linkNote: input.link === null ? null : kitWords(input.locale).linkInBio,
    withLogo: false,
  };
}

export interface LyricMomentPose {
  /** The cover of the row: 1.05 on each downbeat, back in 0.22 s. */
  coverScale: number;
  /** The pill: 1.12 with a spring from the end card. */
  pillScale: number;
  /** True from the end card: the column stops. */
  ended: boolean;
}

/** The pose of the cover row and the pill of a lyric moment at the time `t`. Pure. */
export function lyricMomentPose(
  t: number,
  video: Pick<KitLyricMomentVideo, 'endCardSec' | 'downbeats'>
): LyricMomentPose {
  const last = [...video.downbeats].reverse().find((beat) => beat <= t);
  const since = last === undefined ? Number.POSITIVE_INFINITY : t - last;
  const kick = since < 0.22 ? 1 - since / 0.22 : 0;
  const spring = outBack(progress(t, video.endCardSec, video.endCardSec + 0.36));
  return {
    coverScale: round3(1 + 0.05 * kick * kick),
    pillScale: round3(1 + 0.12 * spring),
    ended: t >= video.endCardSec,
  };
}
